import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { enhancedMonitoringAPI } from '../services/api';
import { DashboardOverview } from '../types';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusIcon,
  EyeIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await enhancedMonitoringAPI.getDashboard();
      setDashboardData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const downloadWordPressPlugin = async () => {
    try {
      const response = await fetch('/api/enhanced/wordpress-plugin/download', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'saas-monitor.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download WordPress plugin');
    }
  };

  const [showPluginInfo, setShowPluginInfo] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'offline':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-green-600';
      case 'offline':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">
            Welcome back, {user?.name}! Here's what's happening with your sites.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/sites"
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add Site
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Online Sites
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {dashboardData.stats?.onlineSites || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Offline Sites
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {dashboardData.stats?.offlineSites || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Sites
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {dashboardData.stats?.totalSites || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Avg Response Time
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {dashboardData.stats?.avgResponseTime || 0}ms
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sites List */}
      <div className="card">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Your Sites
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Monitor the status and performance of all your websites.
          </p>
        </div>
        <div className="border-t border-gray-200">
          {!dashboardData?.sites || dashboardData.sites.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No sites found. Add your first site to get started!</p>
              <Link
                to="/sites"
                className="btn-primary mt-4 inline-flex items-center"
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Add Your First Site
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {dashboardData.sites.map((site: any) => (
                <div key={site.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getStatusIcon(site.status)}
                      <div className="ml-4">
                        <h4 className="text-sm font-medium text-gray-900">
                          {site.name}
                        </h4>
                        <p className="text-sm text-gray-500">{site.url}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-sm">
                        <span className="text-gray-500">Status:</span>
                        <span className={`ml-2 font-medium ${getStatusColor(site.status)}`}>
                          {site.status}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Uptime:</span>
                        <span className="ml-2 font-medium text-gray-900">{site.uptime || 'N/A'}</span>
                      </div>
                      {site.response_time && (
                        <div className="text-sm">
                          <span className="text-gray-500">Response:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {site.response_time}ms
                          </span>
                        </div>
                      )}
                      <Link
                        to={`/sites/${site.id}`}
                        className="btn-secondary inline-flex items-center"
                      >
                        <EyeIcon className="mr-2 h-4 w-4" />
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* WordPress Plugin Section */}
      <div className="card">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                WordPress Plugin
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Install our WordPress plugin for deep site monitoring and analysis.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowPluginInfo(!showPluginInfo)}
                className="btn-secondary inline-flex items-center"
              >
                <InformationCircleIcon className="mr-2 h-4 w-4" />
                Info
              </button>
              <button
                onClick={downloadWordPressPlugin}
                className="btn-primary inline-flex items-center"
              >
                <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                Download Plugin
              </button>
            </div>
          </div>
        </div>
        
        {showPluginInfo && (
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Features</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Real-time security monitoring</li>
                  <li>• Performance tracking and optimization</li>
                  <li>• File integrity monitoring</li>
                  <li>• Login attempt monitoring</li>
                  <li>• Database optimization</li>
                  <li>• Automatic security scans</li>
                  <li>• Performance alerts</li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Installation Steps</h4>
                <ol className="text-sm text-gray-600 space-y-2">
                  <li>1. Download the plugin file (saas-monitor.php)</li>
                  <li>2. Upload to your WordPress /wp-content/plugins/ directory</li>
                  <li>3. Activate the plugin in your WordPress admin</li>
                  <li>4. Configure your API key and Site ID in the plugin settings</li>
                  <li>5. Start monitoring your site in real-time</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;